import React, { useState } from 'react';
import { Card, Form, Button, Alert, ProgressBar } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { uploadCompanyData } from '../../api/companyApi';
import * as XLSX from 'xlsx';

const CompanyUpload = ({ onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isValidFile, setIsValidFile] = useState(true);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Validate file type
      if (!selectedFile.name.endsWith('.xlsx')) {
        setError('Please select a valid Excel (.xlsx) file.');
        setFile(null);
        setIsValidFile(false);
        return;
      }
      
      // Validate the Excel file structure
      validateExcelFile(selectedFile);
    }
  };

  const validateExcelFile = async (selectedFile) => {
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Check for required sheets
        const requiredSheets = ['Companies', 'Panels'];
        const hasRequiredSheets = requiredSheets.every(sheet => 
          workbook.SheetNames.includes(sheet)
        );
        
        if (!hasRequiredSheets) {
          setError('Excel file must contain both "Companies" and "Panels" sheets.');
          setFile(null);
          setIsValidFile(false);
          return;
        }
        
        // Validate Companies sheet
        const companiesSheet = workbook.Sheets['Companies'];
        const companiesData = XLSX.utils.sheet_to_json(companiesSheet);
        
        if (companiesData.length === 0) {
          setError('Companies sheet is empty. Please add company data.');
          setFile(null);
          setIsValidFile(false);
          return;
        }
        
        // Check required columns in Companies sheet
        const requiredCompanyColumns = ['company_name', 'visit_date', 'round_number', 'start_time', 'duration'];
        const firstRow = companiesData[0];
        const missingCompanyColumns = requiredCompanyColumns.filter(col => !(col in firstRow));
        
        if (missingCompanyColumns.length > 0) {
          setError(`Companies sheet is missing required columns: ${missingCompanyColumns.join(', ')}`);
          setFile(null);
          setIsValidFile(false);
          return;
        }
        
        // Validate Panels sheet
        const panelsSheet = workbook.Sheets['Panels'];
        const panelsData = XLSX.utils.sheet_to_json(panelsSheet);
        
        if (panelsData.length === 0) {
          setError('Panels sheet is empty. Please add panel data.');
          setFile(null);
          setIsValidFile(false);
          return;
        }
        
        // Check required columns in Panels sheet - now simplified
        const requiredPanelColumns = ['company_name', 'panel_number'];
        const firstPanelRow = panelsData[0];
        const missingPanelColumns = requiredPanelColumns.filter(col => !(col in firstPanelRow));
        
        if (missingPanelColumns.length > 0) {
          setError(`Panels sheet is missing required columns: ${missingPanelColumns.join(', ')}`);
          setFile(null);
          setIsValidFile(false);
          return;
        }
        
        // Validate relationships between Companies and Panels
        const companyNames = new Set(companiesData.map(c => c.company_name));
        const invalidPanels = panelsData.filter(p => !companyNames.has(p.company_name));
        
        if (invalidPanels.length > 0) {
          setError(`Panels sheet contains panels for companies that don't exist in Companies sheet: ${invalidPanels.map(p => p.company_name).join(', ')}`);
          setFile(null);
          setIsValidFile(false);
          return;
        }
        
        // If all validations pass
        setFile(selectedFile);
        setError('');
        setIsValidFile(true);
      };
      
      reader.readAsArrayBuffer(selectedFile);
    } catch (error) {
      console.error('Error validating Excel file:', error);
      setError('Error validating Excel file. Please check the file format.');
      setFile(null);
      setIsValidFile(false);
    }
  };

  // const validateExcelFile = async (selectedFile) => {
  //   try {
  //     const reader = new FileReader();
  //     reader.onload = (e) => {
  //       const data = new Uint8Array(e.target.result);
  //       const workbook = XLSX.read(data, { type: 'array' });
        
  //       // Check for required sheets
  //       const requiredSheets = ['Companies', 'Panels'];
  //       const hasRequiredSheets = requiredSheets.every(sheet => 
  //         workbook.SheetNames.includes(sheet)
  //       );
        
  //       if (!hasRequiredSheets) {
  //         setError('Excel file must contain both "Companies" and "Panels" sheets.');
  //         setFile(null);
  //         setIsValidFile(false);
  //         return;
  //       }
        
  //       // Validate Companies sheet
  //       const companiesSheet = workbook.Sheets['Companies'];
  //       const companiesData = XLSX.utils.sheet_to_json(companiesSheet);
        
  //       if (companiesData.length === 0) {
  //         setError('Companies sheet is empty. Please add company data.');
  //         setFile(null);
  //         setIsValidFile(false);
  //         return;
  //       }
        
  //       // Check required columns in Companies sheet
  //       const requiredCompanyColumns = ['company_name', 'visit_date', 'round_number', 'start_time', 'end_time', 'duration'];
  //       const firstRow = companiesData[0];
  //       const missingCompanyColumns = requiredCompanyColumns.filter(col => !(col in firstRow));
        
  //       if (missingCompanyColumns.length > 0) {
  //         setError(`Companies sheet is missing required columns: ${missingCompanyColumns.join(', ')}`);
  //         setFile(null);
  //         setIsValidFile(false);
  //         return;
  //       }
        
  //       // Validate Panels sheet
  //       const panelsSheet = workbook.Sheets['Panels'];
  //       const panelsData = XLSX.utils.sheet_to_json(panelsSheet);
        
  //       if (panelsData.length === 0) {
  //         setError('Panels sheet is empty. Please add panel data.');
  //         setFile(null);
  //         setIsValidFile(false);
  //         return;
  //       }
        
  //       // Check required columns in Panels sheet
  //       const requiredPanelColumns = ['company_name', 'panel_number', 'interviewer_name', 'panel_place'];
  //       const firstPanelRow = panelsData[0];
  //       const missingPanelColumns = requiredPanelColumns.filter(col => !(col in firstPanelRow));
        
  //       if (missingPanelColumns.length > 0) {
  //         setError(`Panels sheet is missing required columns: ${missingPanelColumns.join(', ')}`);
  //         setFile(null);
  //         setIsValidFile(false);
  //         return;
  //       }
        
  //       // Validate relationships between Companies and Panels
  //       const companyNames = new Set(companiesData.map(c => c.company_name));
  //       const invalidPanels = panelsData.filter(p => !companyNames.has(p.company_name));
        
  //       if (invalidPanels.length > 0) {
  //         setError(`Panels sheet contains panels for companies that don't exist in Companies sheet: ${invalidPanels.map(p => p.company_name).join(', ')}`);
  //         setFile(null);
  //         setIsValidFile(false);
  //         return;
  //       }
        
  //       // If all validations pass
  //       setFile(selectedFile);
  //       setError('');
  //       setIsValidFile(true);
  //     };
      
  //     reader.readAsArrayBuffer(selectedFile);
  //   } catch (error) {
  //     console.error('Error validating Excel file:', error);
  //     setError('Error validating Excel file. Please check the file format.');
  //     setFile(null);
  //     setIsValidFile(false);
  //   }
  // };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }
    
    try {
      setUploading(true);
      setUploadProgress(0);
      setError('');
      setSuccess('');
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload the file with progress tracking
      const response = await uploadCompanyData(formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      setSuccess(`Upload successful! ${response.companies_created?.length || 0} companies created.`);
      toast.success('Company data uploaded successfully');
      setFile(null);
      
      // Reset file input
      document.getElementById('company-file-upload').value = '';
      
      // Call the callback function if provided
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.message || 'An error occurred during upload. Please try again.');
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const generateTemplate = () => {
    // Create workbook with two sheets: Companies and Panels
    const wb = XLSX.utils.book_new();
    
    // Create Companies sheet with required columns
    const companiesData = [
      {
        company_name: 'Google',
        visit_date: '2025-04-15',
        round_number: 1,
        start_time: '09:00',
        duration: 30
      },
      {
        company_name: 'Microsoft',
        visit_date: '2025-04-16',
        round_number: 1,
        start_time: '10:00',
        duration: 45
      }
    ];
      // Create Panels sheet with required columns (simplified format)
  const panelsData = [
    {
      company_name: 'Google',
      panel_number: 3
    },
    {
      company_name: 'Microsoft',
      panel_number: 2
    }
  ];
  
  // Add sheets to workbook
  const companiesSheet = XLSX.utils.json_to_sheet(companiesData);
  const panelsSheet = XLSX.utils.json_to_sheet(panelsData);
  
  XLSX.utils.book_append_sheet(wb, companiesSheet, "Companies");
  XLSX.utils.book_append_sheet(wb, panelsSheet, "Panels");
  
  // Generate and download file
  XLSX.writeFile(wb, "company_upload_template.xlsx");
};

  // const generateTemplate = () => {
  //   // Create workbook with two sheets: Companies and Panels
  //   const wb = XLSX.utils.book_new();
    
  //   // Create Companies sheet with required columns
  //   const companiesData = [
  //     {
  //       company_name: 'Google',
  //       visit_date: '2025-04-15',
  //       round_number: 1,
  //       start_time: '09:00',
  //       end_time: '12:00',
  //       duration: 30
  //     },
  //     {
  //       company_name: 'Microsoft',
  //       visit_date: '2025-04-16',
  //       round_number: 1,
  //       start_time: '10:00',
  //       end_time: '13:00',
  //       duration: 45
  //     }
  //   ];
    
  //   // Create Panels sheet with required columns
  //   const panelsData = [
  //     {
  //       company_name: 'Google',
  //       panel_number: 1,
  //       interviewer_name: 'John Doe',
  //       panel_place: 'Room 101'
  //     },
  //     {
  //       company_name: 'Google',
  //       panel_number: 2,
  //       interviewer_name: 'Jane Smith',
  //       panel_place: 'Room 102'
  //     },
  //     {
  //       company_name: 'Microsoft',
  //       panel_number: 1,
  //       interviewer_name: 'Bob Johnson',
  //       panel_place: 'Room 201'
  //     },
  //     {
  //       company_name: 'Microsoft',
  //       panel_number: 2,
  //       interviewer_name: 'Alice Brown',
  //       panel_place: 'Room 202'
  //     }
  //   ];
    
  //   // Add sheets to workbook
  //   const companiesSheet = XLSX.utils.json_to_sheet(companiesData);
  //   const panelsSheet = XLSX.utils.json_to_sheet(panelsData);
    
  //   XLSX.utils.book_append_sheet(wb, companiesSheet, "Companies");
  //   XLSX.utils.book_append_sheet(wb, panelsSheet, "Panels");
    
  //   // Generate and download file
  //   XLSX.writeFile(wb, "company_upload_template.xlsx");
  // };

  return (
    <Card>
      <Card.Header as="h5">Upload Company Data</Card.Header>
      <Card.Body>
        <p className="text-muted mb-4">
          Upload an Excel file containing company information and panel details.
          The file should have two sheets: "Companies" and "Panels" with specific column names.
        </p>
        
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Select Excel File</Form.Label>
            <Form.Control
              id="company-file-upload"
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              disabled={uploading}
            />
            <Form.Text className="text-muted">
              The file must be in the correct format with required columns. 
              <Button 
                variant="link" 
                className="p-0 ms-1" 
                onClick={generateTemplate}
                disabled={uploading}
              >
                Download template
              </Button>
            </Form.Text>
          </Form.Group>
          
          <div className="mb-3">
            <h6>Required Format:</h6>
            <ul className="small text-muted">
              <li><strong>Companies sheet:</strong> company_name, visit_date, round_number, start_time, duration</li>
              <li><strong>Panels sheet:</strong> company_name, panel_number (number of panels to create)</li>
            </ul>
          </div>
          
          {uploading && (
            <div className="mb-3">
              <ProgressBar 
                animated 
                now={uploadProgress} 
                label={`${uploadProgress}%`} 
              />
            </div>
          )}
          
          <div className="d-grid">
            <Button 
              variant="primary" 
              type="submit" 
              disabled={!file || uploading || !isValidFile}
            >
              {uploading ? 'Uploading...' : 'Upload Company Data'}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default CompanyUpload;